// Generates all procedural sprites and loads the supplied avatar image.
// On complete, hands off to IntroScene.

import Phaser from 'phaser';
import { buildAvatarTextures, loadAvatarSource } from '../systems/avatar';
import { generateAllSprites, SPRITE_KEYS } from '../systems/sprites';
import { readParams, GameParams } from '../params';
import { track } from '../systems/analytics';
import { AUDIO_KEYS, loadAudio, generateProceduralSounds, playBgm } from '../systems/audio';

export class BootScene extends Phaser.Scene {
  private params!: GameParams;

  constructor() {
    super('Boot');
  }

  preload() {
    this.params = readParams();
    track('game_loaded', { hasImage: !!this.params.imageUrl, name: this.params.name });

    const overlayBar = document.querySelector('.loading-bar-fill') as HTMLElement | null;
    const overlayStatus = document.querySelector('.loading-status') as HTMLElement | null;
    overlayStatus?.replaceChildren(document.createTextNode('Drawing pixels…'));

    // 1. Queue PNG sprite loads. generateAllSprites() runs in create() after these
    //    complete, so textures.exists() correctly short-circuits the procedural fallback.
    this.load.image(SPRITE_KEYS.floor,       '/sprites/floor.png');
    this.load.image(SPRITE_KEYS.wall,        '/sprites/wall.png');
    this.load.image(SPRITE_KEYS.serverRack,  '/sprites/server_rack.png');
    this.load.image(SPRITE_KEYS.serverRackB, '/sprites/server_rack_b.png');
    this.load.image(SPRITE_KEYS.serverRackC, '/sprites/server_rack_c.png');
    this.load.image(SPRITE_KEYS.serverRackD, '/sprites/server_rack_d.png');
    this.load.image(SPRITE_KEYS.desktop,     '/sprites/desktop.png');
    this.load.image(SPRITE_KEYS.chair,       '/sprites/chair.png');
    this.load.image(SPRITE_KEYS.plant,       '/sprites/plant.png');
    this.load.image(SPRITE_KEYS.scanner,     '/sprites/scanner.png');
    this.load.image(SPRITE_KEYS.crate,       '/sprites/crate.png');
    this.load.image(SPRITE_KEYS.coffee,      '/sprites/coffee.png');
    this.load.image(SPRITE_KEYS.printer,     '/sprites/printer.png');
    this.load.image(SPRITE_KEYS.locker,      '/sprites/locker.png');
    this.load.image(SPRITE_KEYS.whiteboard,  '/sprites/whiteboard.png');
    this.load.image(SPRITE_KEYS.trash,       '/sprites/trash.png');
    this.load.image(SPRITE_KEYS.monitorWall, '/sprites/monitor_wall.png');
    this.load.image(SPRITE_KEYS.floorMat,    '/sprites/floor_mat.png');
    this.load.image(SPRITE_KEYS.door,        '/sprites/door.png');
    this.load.image(SPRITE_KEYS.switch,      '/sprites/switch.png');
    this.load.image(SPRITE_KEYS.cable,       '/sprites/cable.png');
    this.load.image(SPRITE_KEYS.cve,         '/sprites/cve.png');
    this.load.image(SPRITE_KEYS.playerBody,  '/sprites/player_body.png');
    this.load.image(SPRITE_KEYS.playerCarry, '/sprites/player_carry.png');
    this.load.image(SPRITE_KEYS.helmet,      '/sprites/helmet.png');
    this.load.image(SPRITE_KEYS.boss,         '/sprites/boss.png');
    this.load.image(SPRITE_KEYS.bossIdle0,    '/sprites/boss_idle_0.png');
    this.load.image(SPRITE_KEYS.bossIdle1,    '/sprites/boss_idle_1.png');
    this.load.image(SPRITE_KEYS.bossThrow0,   '/sprites/boss_throw_0.png');
    this.load.image(SPRITE_KEYS.bossThrow1,   '/sprites/boss_throw_1.png');
    this.load.image(SPRITE_KEYS.bossAdvance0, '/sprites/boss_advance_0.png');
    this.load.image(SPRITE_KEYS.bossAdvance1, '/sprites/boss_advance_1.png');
    this.load.image(SPRITE_KEYS.bossPhase3_1, '/sprites/boss_phase3_1.png');
    this.load.image(SPRITE_KEYS.bossPhase3_2, '/sprites/boss_phase3_2.png');
    this.load.image(SPRITE_KEYS.exploitedMarker, '/sprites/exploited_marker.png');
    this.load.image(SPRITE_KEYS.codeSnippet0, '/sprites/code_snippet_0.png');
    this.load.image(SPRITE_KEYS.codeSnippet1, '/sprites/code_snippet_1.png');
    this.load.image(SPRITE_KEYS.powerupIonix, '/sprites/powerup_ionix.png');
    this.load.image(SPRITE_KEYS.asset.webserver, '/sprites/asset_webserver.png');
    this.load.image(SPRITE_KEYS.asset.domain,    '/sprites/asset_domain.png');
    this.load.image(SPRITE_KEYS.asset.s3,        '/sprites/asset_s3.png');
    this.load.image(SPRITE_KEYS.asset.api,       '/sprites/asset_api.png');
    this.load.image(SPRITE_KEYS.asset.database,  '/sprites/asset_database.png');
    this.load.image(SPRITE_KEYS.asset.cloud,     '/sprites/asset_cloud.png');
    this.load.image(SPRITE_KEYS.asset.cert,      '/sprites/asset_cert.png');
    this.load.image(SPRITE_KEYS.asset.subdomain, '/sprites/asset_subdomain.png');
    this.load.image(SPRITE_KEYS.asset.email,     '/sprites/asset_email.png');
    this.load.image(SPRITE_KEYS.asset.dns,       '/sprites/asset_dns.png');
    this.load.image(SPRITE_KEYS.asset.dataleak,  '/sprites/asset_dataleak.png');

    loadAudio(this);
    if (overlayBar) overlayBar.style.width = '40%';
    overlayStatus?.replaceChildren(document.createTextNode('Loading agent…'));

    // 2. load avatar (if any). If proxy URL is configured, prefer it; else
    //    direct URL with crossOrigin best-effort.
    const url = this.params.proxiedImageUrl || this.params.imageUrl;
    loadAvatarSource(this, url, () => {
      if (overlayBar) overlayBar.style.width = '85%';
      overlayStatus?.replaceChildren(document.createTextNode('Composing avatar…'));
      buildAvatarTextures(this);
      if (overlayBar) overlayBar.style.width = '100%';
      overlayStatus?.replaceChildren(document.createTextNode('Ready'));
    });
  }

  create() {
    // Procedural fallbacks — run after the loader completes so any PNG loaded
    // above is already in the texture/audio cache and takes precedence.
    generateAllSprites(this);
    generateProceduralSounds(this);

    // Loading is complete: surface the READY button. Clicking it is the
    // user gesture that unlocks the browser's audio context, so we kick off
    // bgmIntro right there — that way the loadscreen track plays from frame
    // zero when the user actively starts the experience.
    const introKey = this.cache.audio.exists(AUDIO_KEYS.bgmIntro)
      ? AUDIO_KEYS.bgmIntro
      : AUDIO_KEYS.bgmMain;

    const overlay = document.getElementById('loading-overlay');
    const readyBtn = document.getElementById('loading-ready-btn');
    const progressBar = document.querySelector<HTMLElement>('.ix-load-progress-bar');

    const startIntro = () => {
      playBgm(this, introKey);
      if (overlay) {
        overlay.style.transition = 'opacity 300ms ease';
        overlay.style.opacity = '0';
        setTimeout(() => {
          overlay.classList.add('hidden');
          this.scene.start('Intro', { params: this.params });
        }, 320);
      } else {
        this.scene.start('Intro', { params: this.params });
      }
    };

    if (readyBtn) {
      // Swap the loading bar out for the button — same slot, no layout shift.
      progressBar?.classList.add('hidden');
      readyBtn.classList.remove('hidden');
      readyBtn.addEventListener('click', startIntro, { once: true });
    } else {
      // No button in DOM (shouldn't happen) — fall through with a delay.
      setTimeout(startIntro, 600);
    }
  }
}
